import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  Alert,
  Badge,
  Box,
  Center,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { useAuthUser } from "react-auth-kit";
import { useTranslation } from "react-i18next";

import { Page } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import { useCellRender } from "../../hooks/useCellRender";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { resolveEntityPath } from "../../utils/resolveEntityPath";
import { EDD_INDICATOR_LABELS } from "../../helpers/eddIndicatorLabels";

const ReportSection = ({ title, description, count, tone, children }) => (
  <Paper
    withBorder
    radius="md"
    sx={(theme) => ({
      overflow: "hidden",
      height: "100%",
      backgroundColor:
        theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
      borderColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[4]
          : theme.colors.gray[3],
    })}
  >
    <Group
      position="apart"
      align="flex-start"
      noWrap
      spacing="md"
      px="md"
      py="sm"
      sx={(theme) => ({
        backgroundColor:
          theme.colorScheme === "dark"
            ? theme.colors.dark[6]
            : theme.fn.rgba(theme.colors[tone][6], 0.08),
        borderBottom: `1px solid ${
          theme.colorScheme === "dark"
            ? theme.colors.dark[4]
            : theme.colors.gray[2]
        }`,
        borderLeft: `4px solid ${theme.colors[tone][6]}`,
      })}
    >
      <Box sx={{ minWidth: 0 }}>
        <Text size="sm" weight={700}>
          {title}
        </Text>
        {description ? (
          <Text size="xs" color="dimmed" mt={2}>
            {description}
          </Text>
        ) : null}
      </Box>
      <Badge color={tone} variant="light" sx={{ flexShrink: 0 }}>
        {count}
      </Badge>
    </Group>
    <Box p="md">{children}</Box>
  </Paper>
);

const ReportTable = ({ children }) => (
  <Box sx={{ overflowX: "auto" }}>
    <Table
      fontSize="sm"
      striped
      highlightOnHover
      sx={{
        "& th, & td": { verticalAlign: "top" },
        "& th.num, & td.num": { textAlign: "right", whiteSpace: "nowrap" },
      }}
    >
      {children}
    </Table>
  </Box>
);

const ClientLink = ({ row, onOpen }) => {
  const code = row.clientCode || row.code;
  return (
    <Text
      component="a"
      size="sm"
      style={{ textDecoration: "none", cursor: "pointer" }}
      onClick={(event) => onOpen(event, row)}
    >
      {code || row.name || "-"}
      {row.name ? (
        <Text component="span" color="dimmed" size="xs">
          {" "}
          {row.name}
        </Text>
      ) : null}
    </Text>
  );
};

const EddExceptionReport = () => {
  const [api] = useServerApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [cellRender] = useCellRender();
  const auth = useAuthUser();
  const tabNav = useTabNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const clientPath = resolveEntityPath("client", auth()?.userRole);
  const money = (value) => cellRender.Currency(value, "HKD");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const result = await apiRef.current.Client.getEddExceptionReport();
      if (cancelled) return;
      if (result?.success) setReport(result.data);
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const openClient = (event, row) => {
    event.preventDefault();
    if (!row?.clientId || !clientPath) return;
    if (tabNav.enabled) {
      tabNav.openDetail({
        pathname: clientPath,
        id: row.clientId,
        mode: "edit",
        label: row.clientCode || row.code || row.name,
      });
      return;
    }
    window.open(`${clientPath}?id=${row.clientId}&mode=edit`, "_blank");
  };

  const basic = report?.basic || [];
  const closest = report?.backup?.closestTo2M || [];
  const scores = report?.backup?.highestScore || [];
  const withdrawals = report?.withdrawals?.top || [];

  return (
    <Page title="EDD Exception Report">
      <Text size="sm" color="dimmed" mb="lg">
        {t(
          "Suspicious transaction indicators for enhanced due diligence."
        )}
      </Text>
      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : !report ? (
        <Alert color="red">{t("Unable to load the exception report.")}</Alert>
      ) : (
        <Stack spacing="lg">
          <ReportSection
            title={t("Basic criteria")}
            description={t(
              "Clients who meet a primary enhanced due diligence indicator."
            )}
            count={basic.length}
            tone="cyan"
          >
            {basic.length === 0 ? (
              <Text size="sm" color="dimmed">
                {t("No client currently meets a basic EDD indicator.")}
              </Text>
            ) : (
              <ReportTable>
                <thead>
                  <tr>
                    <th>{t("Client")}</th>
                    <th>{t("Indicator")}</th>
                    <th className="num">{t("Life/ILAS NB")}</th>
                    <th className="num">{t("Lump Sum Premium")}</th>
                    <th className="num">{t("Subscriptions")}</th>
                    <th className="num">{t("Subscription Amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {basic.map((row) => (
                    <tr key={row.clientId}>
                      <td>
                        <ClientLink row={row} onOpen={openClient} />
                      </td>
                      <td>
                        {(row.reasons || []).map((key) => (
                          <Text key={key} size="xs">
                            {t(EDD_INDICATOR_LABELS[key] || key)}
                          </Text>
                        ))}
                      </td>
                      <td className="num">{row.lifeIlas?.nbCount12Months ?? 0}</td>
                      <td className="num">
                        {money(row.lifeIlas?.lumpSumPremiumHkd)}
                      </td>
                      <td className="num">{row.subscription?.count ?? 0}</td>
                      <td className="num">
                        {money(row.subscription?.totalAmountHkd)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ReportTable>
            )}
          </ReportSection>

          <Box>
            <Text size="xs" weight={700} color="dimmed" transform="uppercase">
              {t("Backup picks")}
            </Text>
            <Text size="xs" color="dimmed" mt={4} mb="sm">
              {t(
                "Shown only when nobody meets the matching basic rule."
              )}
            </Text>
            <SimpleGrid
              cols={2}
              spacing="md"
              breakpoints={[{ maxWidth: "md", cols: 1 }]}
            >
              <ReportSection
                title={t("Closest to HKD 2M")}
                description={t("Subscription total nearest to HKD 2M.")}
                count={closest.length}
                tone="orange"
              >
                {closest.length === 0 ? (
                  <Text size="sm" color="dimmed">
                    {t(
                      "Not used. At least one subscription amount exceeds HKD 2M."
                    )}
                  </Text>
                ) : (
                  <ReportTable>
                    <thead>
                      <tr>
                        <th>{t("Client")}</th>
                        <th className="num">{t("Subscriptions")}</th>
                        <th className="num">{t("Subscription Amount")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {closest.map((row) => (
                        <tr key={`close-${row.clientId}`}>
                          <td>
                            <ClientLink row={row} onOpen={openClient} />
                          </td>
                          <td className="num">{row.count}</td>
                          <td className="num">{money(row.totalAmountHkd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </ReportTable>
                )}
              </ReportSection>

              <ReportSection
                title={t("Highest count times amount")}
                description={t(
                  "Highest subscription count multiplied by amount."
                )}
                count={scores.length}
                tone="violet"
              >
                {scores.length === 0 ? (
                  <Text size="sm" color="dimmed">
                    {t(
                      "Not used. At least one client has more than 5 subscriptions and more than HKD 1M."
                    )}
                  </Text>
                ) : (
                  <ReportTable>
                    <thead>
                      <tr>
                        <th>{t("Client")}</th>
                        <th className="num">{t("Subscriptions")}</th>
                        <th className="num">{t("Subscription Amount")}</th>
                        <th className="num">{t("Score")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scores.map((row) => (
                        <tr key={`score-${row.clientId}`}>
                          <td>
                            <ClientLink row={row} onOpen={openClient} />
                          </td>
                          <td className="num">{row.count}</td>
                          <td className="num">{money(row.totalAmountHkd)}</td>
                          <td className="num">{money(row.score)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </ReportTable>
                )}
              </ReportSection>
            </SimpleGrid>
          </Box>

          <ReportSection
            title={`${t("Top 5 withdrawals")} (${
              report.withdrawals?.month || moment().format("YYYY-MM")
            })`}
            description={t("Largest withdrawals recorded this month.")}
            count={withdrawals.length}
            tone="teal"
          >
            {withdrawals.length === 0 ? (
              <Text size="sm" color="dimmed">
                {t("No withdrawals this month.")}
              </Text>
            ) : (
              <ReportTable>
                <thead>
                  <tr>
                    <th className="num">{t("Rank")}</th>
                    <th>{t("Client")}</th>
                    <th>{t("Service")}</th>
                    <th>{t("Order")}</th>
                    <th>{t("Date")}</th>
                    <th className="num">{t("Amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map((row) => (
                    <tr key={row._id || `${row.clientId}-${row.rank}`}>
                      <td className="num">{row.rank}</td>
                      <td>
                        <ClientLink row={row} onOpen={openClient} />
                      </td>
                      <td>{row.code || "-"}</td>
                      <td>{row.orderCode || "-"}</td>
                      <td>
                        {row.date ? moment(row.date).format("YYYY-MM-DD") : "-"}
                      </td>
                      <td className="num">{money(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </ReportTable>
            )}
          </ReportSection>
        </Stack>
      )}
    </Page>
  );
};

export default EddExceptionReport;
